import { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Skeleton,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { StarIcon, ViewIcon } from "@chakra-ui/icons";
import Layout from "../components/Layout";
import LastEvents from "../components/LastEvents";
import OtherEvents from "../components/OtherEvents";
import CertificateModal from "../components/CertificateModal";
import { useAuth } from "../context/AuthContext";
import client from "../utils/client";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

const isPast = (dateStr) => dateStr && new Date(dateStr) < new Date();

const formatDateBR = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return format(new Date(dateStr), "dd/MM/yyyy", { locale: ptBR });
  } catch {
    return dateStr;
  }
};

const MyCertificates = ({ userId, userName }) => {
  const [certs, setCerts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    client
      .get("/events?page=1")
      .then(async (res) => {
        // Fetch all pages if needed — for typical usage one page (25) is enough
        const all = res.data.eventItems || [];
        const totalPages = res.data.totalPages || 1;

        let extra = [];
        for (let p = 2; p <= totalPages; p++) {
          const r = await client.get(`/events?page=${p}`);
          extra = extra.concat(r.data.eventItems || []);
        }

        const allEvents = [...all, ...extra];

        const available = allEvents
          .filter((ev) => isPast(ev.eventDate))
          .flatMap((ev) => {
            const results = [];
            const isListener = (ev.listeners || []).some((l) => l.listenerId === userId);
            const isSpeaker = (ev.speakers || []).some((s) => s.speakerId === userId);
            if (isListener) results.push({ ...ev, modality: "listener" });
            if (isSpeaker) results.push({ ...ev, modality: "speaker" });
            return results;
          })
          .sort((a, b) => new Date(b.eventDate) - new Date(a.eventDate));

        setCerts(available);
      })
      .catch(() => setCerts([]))
      .finally(() => setIsLoading(false));
  }, [userId]);

  const handleOpen = (cert) => {
    setSelected({
      userName,
      eventName: cert.name,
      eventDate: cert.eventDate,
      theme: cert.theme || null,
      modality: cert.modality,
      eventImageUrl: cert.image
        ? `${process.env.REACT_APP_S3_BASEURL}/events_images/${cert.image}`
        : null,
    });
    onOpen();
  };

  return (
    <>
      <CertificateModal isOpen={isOpen} onClose={onClose} cert={selected} />

      <Box py={[6, 10]} px={[4, 6, 8]} maxW="1400px" mx="auto">
        <Heading
          fontSize={["1.4rem", "1.8rem"]}
          color="green.700"
          mb={1}
          borderLeft="4px solid"
          borderColor="green.400"
          pl={4}
        >
          Meus Certificados
        </Heading>
        <Text color="gray.500" pl={4} mb={8} fontSize="0.95rem">
          Eventos em que você participou — disponíveis para download
        </Text>

        <Skeleton isLoaded={!isLoading}>
          {certs.length === 0 ? (
            <Box
              bg="white"
              borderRadius="1.25rem"
              p={8}
              textAlign="center"
              border="1px solid"
              borderColor="gray.100"
              boxShadow="0 2px 12px rgba(0,0,0,0.05)"
            >
              <Text fontSize="2rem" mb={2}>🎓</Text>
              <Text color="gray.500" fontWeight={600}>Nenhum certificado disponível ainda.</Text>
              <Text color="gray.400" fontSize="0.85rem" mt={1}>
                Os certificados ficam disponíveis após a data de realização dos eventos em que você se inscreveu.
              </Text>
            </Box>
          ) : (
            <Flex flexDir="column" gap={3}>
              {certs.map((cert) => (
                <Box
                  key={`${cert.eventId}-${cert.modality}`}
                  bg="white"
                  borderRadius="1.25rem"
                  border="1px solid"
                  borderColor="gray.100"
                  boxShadow="0 2px 10px rgba(0,0,0,0.06)"
                  p={[4, 5]}
                  _hover={{ borderColor: "green.200", boxShadow: "0 4px 18px rgba(0,0,0,0.09)" }}
                  transition="all 0.2s"
                >
                  <Flex
                    alignItems={["flex-start", "center"]}
                    justifyContent="space-between"
                    flexDir={["column", "row"]}
                    gap={4}
                  >
                    <Flex flexDir="column" gap={1}>
                      <Flex alignItems="center" gap={2} flexWrap="wrap">
                        <Text fontWeight={700} fontSize="1rem" color="gray.800">
                          {cert.name}
                        </Text>
                        <Badge
                          colorScheme={cert.modality === "speaker" ? "orange" : "green"}
                          fontSize="0.68rem"
                          borderRadius="full"
                          px={2}
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          {cert.modality === "speaker" ? (
                            <><StarIcon boxSize={2} /> Palestrante</>
                          ) : (
                            "Participante"
                          )}
                        </Badge>
                      </Flex>
                      {cert.theme && (
                        <Text fontSize="0.82rem" color="gray.500" fontStyle="italic">
                          {cert.theme}
                        </Text>
                      )}
                      <Text fontSize="0.78rem" color="gray.400">
                        Realizado em {formatDateBR(cert.eventDate)}
                      </Text>
                    </Flex>

                    <Button
                      leftIcon={<ViewIcon />}
                      colorScheme="green"
                      variant="outline"
                      size="sm"
                      px={5}
                      flexShrink={0}
                      onClick={() => handleOpen(cert)}
                    >
                      Ver e baixar certificado
                    </Button>
                  </Flex>
                </Box>
              ))}
            </Flex>
          )}
        </Skeleton>
      </Box>
    </>
  );
};

const Events = () => {
  const auth = useAuth();

  return (
    <Layout>
      <Box>
        {auth?.user?.id ? (
          <>
            <LastEvents />
            <Box py={[6, 10]} px={[4, 6, 8]} maxW="1400px" mx="auto">
              <Heading
                fontSize={["1.6rem", "2rem"]}
                color="green.700"
                mb={1}
                borderLeft="4px solid"
                borderColor="green.400"
                pl={4}
              >
                Todos os Eventos
              </Heading>
              <Text color="gray.500" pl={4} mb={8} fontSize="1rem">
                Encontre o evento que você procura
              </Text>
              <OtherEvents />
            </Box>

            <Box bg="gray.50" borderTop="1px solid" borderColor="gray.100">
              <MyCertificates userId={auth.user.id} userName={auth.user.username} />
            </Box>
          </>
        ) : (
          <Box py={[8, 12]} px={[4, 6, 8]} maxW="1400px" mx="auto">
            <Heading
              fontSize={["1.6rem", "2rem"]}
              color="green.700"
              mb={1}
              borderLeft="4px solid"
              borderColor="green.400"
              pl={4}
            >
              Eventos Públicos
            </Heading>
            <Text color="gray.500" pl={4} mb={8} fontSize="1rem">
              Confira os eventos abertos ao público
            </Text>
            <OtherEvents publicEvents={true} />
          </Box>
        )}
      </Box>
    </Layout>
  );
};

export default Events;
